/**
 * RelarkFounderNote
 * -----------------
 * Compact founder-voice section. Sits between social proof and the
 * compliance/security block on the home page so the trust cascade reads:
 *   "others trust it" → "and here is why it was built" → "and here is how
 *   we protect your data".
 *
 * Single-quote, single-placement. Carelog-style human anchor in an
 * otherwise B2B-heavy second half of the page.
 */

import { useTranslation } from "react-i18next";
import { Quote } from "lucide-react";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";

export default function RelarkFounderNote() {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn(0.2);

  return (
    <section className="py-12 sm:py-16">
      <div ref={ref} className="mx-auto max-w-2xl px-5">
        <div className={`text-center max-w-xl mx-auto mb-8 ${fadeCls(isVisible)}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
            {t("relark.founder.eyebrow")}
          </p>
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t("relark.founder.title")}
          </h2>
        </div>
        <figure
          className={`relative rounded-2xl border border-primary/20 bg-card px-6 py-7 sm:px-8 sm:py-8 shadow-sm ${fadeCls(isVisible)}`}
        >
          <Quote
            className="absolute -top-3 left-6 h-6 w-6 text-primary/70 bg-background px-1"
            aria-hidden
          />
          <div className="flex items-start gap-4">
            {/* Initial avatar */}
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-lg ${fadeCls(isVisible)}`}
              style={fadeStyle(120)}
              aria-hidden
            >
              M
            </div>
            <div className="min-w-0">
              <blockquote
                className={`text-base sm:text-lg italic text-foreground/90 leading-relaxed ${fadeCls(isVisible)}`}
                style={fadeStyle(180)}
              >
                {t("relark.founder.quote")}
              </blockquote>
              <figcaption
                className={`mt-3 text-xs sm:text-sm font-medium text-muted-foreground ${fadeCls(isVisible)}`}
                style={fadeStyle(260)}
              >
                — {t("relark.founder.quoteAttribution")}
              </figcaption>
            </div>
          </div>
        </figure>
      </div>
    </section>
  );
}
